import { Injectable, OnDestroy } from '@angular/core';
import { Scene, Mesh, Line, Vector2, Object3D } from 'three';
import { Subject, BehaviorSubject } from 'rxjs';
import {
  BoundingBox2,
  BoundingBox2FromCenterSize,
} from '../share/boundingbox2';
import { CanvasService } from './canvas.service';

@Injectable()
export class SceneService implements OnDestroy {
  readonly scene = new Scene();
  private renders = new Subject<void>();
  readonly render$ = this.renders.asObservable();
  private sizes = new BehaviorSubject<BoundingBox2>(
    new BoundingBox2FromCenterSize(new Vector2(0, 0), new Vector2(10, 10)),
  );
  readonly size$ = this.sizes.asObservable();
  get size() {
    return this.sizes.value;
  }
  constructor(private canvas: CanvasService) {}
  add(entity: Object3D) {
    this.scene.add(entity);
  }
  remove(entity: Object3D) {
    this.scene.remove(entity);
  }
  resize(box: BoundingBox2) {
    this.sizes.next(box);
  }
  ngOnDestroy() {
    this.renders.complete();
    this.sizes.complete();
  }
  clientSize2SceneSize(client: Vector2): Vector2 {
    return new Vector2(
      (client.x / this.canvas.size.size.x) * this.size.size.x,
      (client.y / this.canvas.size.size.y) * this.size.size.y,
    );
  }
  clientPosition2ScenePosition(client: Vector2): Vector2 {
    return relativeCoordinate(
      this.canvas.size,
      this.size,
      new Vector2(client.x, client.y),
    );
  }
  scenePosition2ClientPotision(scene: Vector2): Vector2{
    return relativeCoordinate(
      this.size,
      this.canvas.size,
      new Vector2(scene.x, scene.y),
    );
  }
}

function relativeCoordinate(
  source: BoundingBox2,
  target: BoundingBox2,
  coordinate: Vector2,
): Vector2 {
  return new Vector2(
    linearTrans(
      coordinate.x,
      source.box.min.x,
      source.box.max.x,
      target.box.min.x,
      target.box.max.x,
    ),
    linearTrans(
      coordinate.y,
      source.box.min.y,
      source.box.max.y,
      target.box.min.y,
      target.box.max.y,
    ),
  );
}

// Transform x in frame s0, s1 to target frame t0, t1
// e.g. linearTrans(3, 6, 0, 1, 2) => 1.5
function linearTrans(
  x: number,
  s0: number,
  s1: number,
  t0: number,
  t1: number,
) {
  return ((x - s0) / (s1 - s0)) * (t1 - t0) + t0;
}
